import { CapabilityButton } from "../CapabilityButton";
import { permissionNames as P } from "../../auth/permissions.generated";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, CheckCircle2, Clock3, DatabaseZap, Play, RefreshCw, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useApiService } from "../../services/apiService";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Progress } from "../ui/progress";

interface SyncProgress {
  resolved?: number;
  processed?: number;
  indexed?: number;
  failed?: number;
}

interface SyncResult extends SyncProgress {
  runId?: string;
  indexName?: string;
  published?: boolean;
  failureReason?: string | null;
}

interface SyncStatus {
  exists: boolean;
  instanceId: string;
  runtimeStatus: string;
  createdAt?: string;
  lastUpdatedAt?: string;
  input?: { runId?: string; indexName?: string; startedAt?: string };
  output?: SyncResult;
  customStatus?: SyncProgress;
  failure?: string | null;
}

type SyncStatusPayload = SyncStatus & {
  input?: SyncStatus["input"] & { RunId?: string; IndexName?: string; StartedAt?: string };
  output?: SyncResult & {
    RunId?: string;
    IndexName?: string;
    Resolved?: number;
    Processed?: number;
    Indexed?: number;
    Failed?: number;
    Published?: boolean;
    FailureReason?: string | null;
  };
  customStatus?: SyncProgress & {
    Resolved?: number;
    Processed?: number;
    Indexed?: number;
    Failed?: number;
  };
};

const normalizeProgress = <T extends SyncProgress>(progress: (T & {
  Resolved?: number;
  Processed?: number;
  Indexed?: number;
  Failed?: number;
}) | undefined): T | undefined => progress && ({
  ...progress,
  resolved: progress.resolved ?? progress.Resolved,
  processed: progress.processed ?? progress.Processed,
  indexed: progress.indexed ?? progress.Indexed,
  failed: progress.failed ?? progress.Failed,
});

const normalizeStatus = (status: SyncStatusPayload): SyncStatus => ({
  ...status,
  input: status.input && {
    runId: status.input.runId ?? status.input.RunId,
    indexName: status.input.indexName ?? status.input.IndexName,
    startedAt: status.input.startedAt ?? status.input.StartedAt,
  },
  output: status.output && {
    ...normalizeProgress(status.output),
    runId: status.output.runId ?? status.output.RunId,
    indexName: status.output.indexName ?? status.output.IndexName,
    published: status.output.published ?? status.output.Published,
    failureReason: status.output.failureReason ?? status.output.FailureReason,
  },
  customStatus: normalizeProgress(status.customStatus),
});

const activeStatuses = new Set(["Pending", "Running", "Suspended"]);
const number = (value: number | undefined) => (value ?? 0).toLocaleString();
const dateTime = (value: string | undefined) => value
  ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "medium" }).format(new Date(value))
  : "—";

export function ProductSearchSyncPage() {
  const api = useApiService();
  const [status, setStatus] = useState<SyncStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [stageOnly, setStageOnly] = useState(false);

  const refresh = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      setStatus(normalizeStatus(await api.get<SyncStatusPayload>("/product-search/sync/status")));
      setError(null);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Unable to load synchronization status.");
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [api]);

  useEffect(() => { void refresh(); }, [refresh]);
  const isActive = status ? activeStatuses.has(status.runtimeStatus) : false;
  useEffect(() => {
    const interval = window.setInterval(() => void refresh(true), isActive ? 5_000 : 30_000);
    return () => window.clearInterval(interval);
  }, [isActive, refresh]);

  const trigger = async () => {
    setStarting(true);
    try {
      const response = await api.post<{ started: boolean }>("/product-search/sync/run" + (stageOnly ? "?publish=false" : ""), {});
      toast.success(response.started ? "Synchronization started" : "Synchronization is already running");
      await refresh(true);
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : "Unable to start synchronization.";
      setError(message);
      toast.error("Synchronization could not be started");
    } finally {
      setStarting(false);
      setConfirmationOpen(false);
    }
  };

  const progress = status?.customStatus ?? status?.output ?? {};
  const resolved = progress.resolved ?? 0;
  const processed = progress.processed ?? (status?.output ? resolved : 0);
  const percent = resolved > 0 ? Math.min(100, Math.round(processed * 100 / resolved)) : 0;
  const presentation = useMemo(() => {
    switch (status?.runtimeStatus) {
      case "Completed": return { label: status.output?.published === false ? "Not published" : "Completed", icon: CheckCircle2, className: "text-emerald-700" };
      case "Failed": return { label: "Failed", icon: XCircle, className: "text-destructive" };
      case "Running": return { label: "Running", icon: RefreshCw, className: "text-blue-700" };
      case "Pending": return { label: "Pending", icon: Clock3, className: "text-amber-700" };
      case "Suspended": return { label: "Suspended", icon: AlertCircle, className: "text-amber-700" };
      default: return { label: status?.exists ? status.runtimeStatus : "Not started", icon: Clock3, className: "text-muted-foreground" };
    }
  }, [status]);
  const StatusIcon = presentation.icon;
  const failure = status?.output?.failureReason || status?.failure;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-2 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Product search synchronization</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Publish eligible InRiver Items, linked packaging and nutrition data to the Elasticsearch product-search alias.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => void refresh()} disabled={loading || starting}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <CapabilityButton variant="outline" capability={P.CatalogSearchSyncRun} onClick={() => { setStageOnly(true); setConfirmationOpen(true); }} disabled={loading || starting || isActive}>Stage index</CapabilityButton>
          <CapabilityButton capability={P.CatalogSearchSyncRun} onClick={() => { setStageOnly(false); setConfirmationOpen(true); }} disabled={loading || starting || isActive}>
            {starting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Run synchronization
          </CapabilityButton>
        </div>
      </div>

      {error && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertTitle>Status unavailable</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>}
      {failure && <Alert variant="destructive"><XCircle className="h-4 w-4" /><AlertTitle>The latest index was not published</AlertTitle><AlertDescription>{failure}</AlertDescription></Alert>}

      <Card>
        <CardHeader className="gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>Current status</CardTitle>
            <CardDescription>Durable workflow instance {status?.instanceId ?? "—"}</CardDescription>
          </div>
          <Badge variant="outline" className={`w-fit gap-2 px-3 py-1 ${presentation.className}`}>
            <StatusIcon className={`h-4 w-4 ${isActive && status?.runtimeStatus === "Running" ? "animate-spin" : ""}`} />
            {loading && !status ? "Loading" : presentation.label}
          </Badge>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm"><span>Processed Items</span><strong>{number(processed)} / {number(resolved)}</strong></div>
            <Progress value={percent} aria-label={`${percent}% processed`} />
            <p className="text-xs text-muted-foreground">{resolved > 0 ? `${percent}% of resolved Items processed` : "Waiting for eligible Item IDs from InRiver"}</p>
          </div>
          <div className="grid gap-px overflow-hidden rounded-lg border bg-border sm:grid-cols-4">
            {[["Resolved", resolved], ["Processed", processed], ["Indexed", progress.indexed], ["Failed", progress.failed]].map(([label, value]) => (
              <div className="bg-background p-4" key={String(label)}><p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p><p className="mt-1 text-2xl font-semibold">{number(value as number | undefined)}</p></div>
            ))}
          </div>
          <dl className="grid gap-4 text-sm sm:grid-cols-2">
            <div><dt className="text-muted-foreground">Run ID</dt><dd className="mt-1 font-medium">{status?.input?.runId ?? status?.output?.runId ?? "—"}</dd></div>
            <div><dt className="text-muted-foreground">Target index</dt><dd className="mt-1 break-all font-medium">{status?.input?.indexName ?? status?.output?.indexName ?? "—"}</dd></div>
            <div><dt className="text-muted-foreground">Started</dt><dd className="mt-1 font-medium">{dateTime(status?.createdAt ?? status?.input?.startedAt)}</dd></div>
            <div><dt className="text-muted-foreground">Last update</dt><dd className="mt-1 font-medium">{dateTime(status?.lastUpdatedAt)}</dd></div>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><DatabaseZap className="h-5 w-5" />Publication behavior</CardTitle></CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          A versioned index is built and validated first. The <code className="rounded bg-muted px-1.5 py-0.5 text-foreground">product-search</code> alias changes atomically only after validation succeeds; a failed run leaves the previously published index active.
        </CardContent>
      </Card>

      <AlertDialog open={confirmationOpen} onOpenChange={setConfirmationOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>{stageOnly ? "Stage a product search index?" : "Run product search synchronization?"}</AlertDialogTitle><AlertDialogDescription>{stageOnly ? "Build and validate a new index without changing the current search aliases. Publication can be completed in Kibana after review." : "This starts a full rebuild from InRiver. The current search index remains available until the new index passes validation."}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><CapabilityButton capability={P.CatalogSearchSyncRun} component={AlertDialogAction} onClick={() => void trigger()}>Start synchronization</CapabilityButton></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
